import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Linking,
  ScrollView,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import ProgressCircle from 'react-native-progress-circle';
import ShimmerPlaceHolder from 'react-native-shimmer-placeholder';
import FastImageView from '../FastImageView';
import { getScreenWidth } from '../../utils/LayoutUtility';
import {
  fetchSelfieHistroyAttributes,
  saveSelfieData,
} from '../../actions/SkinSelfieAnalysisActions';
import Utility from '../../utils/Utility';
import { PERMISSION_STORE } from '../../config/Constants';
import { SCREEN_CONSTANTS, DOWNLOAD_APP_MODAL_TYPE } from '../../config/ScreenConstants';
import { isBlank, isDesktop, isPresent, isWeb, isWebAndroid, isWebIOS } from '../../utils/BooleanUtility';
import images from '../../theme/Images';
import { extractDate } from '../../utils/DateAndTimeUtility';
import memoize from 'lodash/memoize';
import colors from '../../theme/Colors';
import { jsonParser } from '../../utils/JsonUtility';
import RemoteConfig from '../../utils/RemoteConfig';
import { REMOTE_CONFIG_KEYS } from '../../config/Constants';

const SCREEN_WIDTH = getScreenWidth();
const ITEM_WIDTH = Math.min(200, SCREEN_WIDTH * 0.4);
const ITEM_HEIGHT = ITEM_WIDTH;
const SPACING = isDesktop() ? 16 : -ITEM_WIDTH * 0.35; // Negative spacing for overlap on mobile

const UploadNewSelfie = React.memo(({ styling, onPress }) => {
  const style = useMemo(() => [
    styles.image,
    styles.uploadNewSelfieButton,
    styling,
  ], [styling]);

  return (
    <TouchableOpacity style={style} onPress={onPress}>
      <FastImageView source={images.plus_icon} style={styles.imageStyle} />
    </TouchableOpacity>
  );
});

const HistoryComponent = () => {
  const navigation = useNavigation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);
  const dispatch = useDispatch();
  const [carouselData, setCarouselData] = useState([]);
  const [showShimmer, setShowShimmer] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);
  const userSelfieData = useSelector(
    (state) => state.UserAccountInfo.selfieAnalysis,
  );

  const handleClickOnSelfie = (index) => {
    navigation.navigate(SCREEN_CONSTANTS.PAST_SKIN_ANALYSIS, { index });
  };

  const handleFetchResult = () => {
    const params = { startDate: '', endDate: '', has_orbo_content: false };
    dispatch(
      fetchSelfieHistroyAttributes(params, (success, response) => {
        if (success) {
          const extractedData = response.objects.map((item) => ({
            created_at: item?.data?.attributes?.created_at,
            skin_score: item?.data?.attributes?.skin_score,
            image: item?.data?.attributes?.image,
          }));
          const UploadNewSelfieData = {
            title: 'uploadNewSelfie',
          };
          if (isPresent(extractedData)) {
            setCarouselData([UploadNewSelfieData, ...extractedData]);
          }
        } else {
          console.error('API Error');
        }
      }),
    );
  };

  const navigateToSelfie = useCallback(() => {
    if (isDesktop()) {
      navigation.navigate(SCREEN_CONSTANTS.APP_INSTALL_PROMPT, {
        from: location.pathname,
      });
      return;
    }
    if (isWebIOS() || isWebAndroid()) {
      const data =
        jsonParser(
          RemoteConfig.getValue(
            REMOTE_CONFIG_KEYS.text_message_for_download_modal,
          ),
        ) || {};
      const { title = '', subtitle = '' } =
        data[DOWNLOAD_APP_MODAL_TYPE.selfie] || {};
      navigation.navigate(SCREEN_CONSTANTS.DOWNLOAD_APP_MODAL, {
        title: title,
        subtitle: subtitle,
      });
    }
    dispatch(saveSelfieData({ uploadedSelfieStatus: '' }));
    Utility.checkPermissionStatic(
      PERMISSION_STORE.camera,
      true,
      (permissionStatus) => {
        if (
          permissionStatus === 'granted' ||
          permissionStatus === 'authorized'
        ) {
          navigation.navigate(SCREEN_CONSTANTS.TAKE_SKIN_SELFIE, {
            previousScreen: SCREEN_CONSTANTS.FOR_YOU,
          });
        } else if (
          permissionStatus === 'never_ask_again' ||
          permissionStatus === 'denied'
        ) {
          Linking.openSettings();
        }
      },
    );
  }, [dispatch, navigation]);

  const getColorForOverAllScore = (score) => {
    if (score <= 40) {
      return colors.selfie_red;
    } else if (score <= 70) {
      return colors.selfie_yellow;
    } else {
      return colors.selfie_green;
    }
  };

  useFocusEffect(
    useCallback(() => {
      handleFetchResult();
    }, [userSelfieData]),
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowShimmer(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleScroll = (event) => {
    const scrollX = event.nativeEvent.contentOffset.x;
    setScrollPosition(scrollX);
    const viewWidth = event.nativeEvent.layoutMeasurement.width;
    const itemWidth = ITEM_WIDTH + SPACING;
    const contentWidth = itemWidth * carouselData.length;
    const maxOffset = contentWidth - viewWidth;
    const centerPosition = scrollX + viewWidth / 2;
    const centerIndex = Math.floor(centerPosition / itemWidth);

    if (scrollX <= 0) {
      setCurrentIndex(0);
    }
    else if (scrollX >= maxOffset - 1) {
      setCurrentIndex(carouselData.length - 1);
    }
    else {
      setCurrentIndex(Math.max(0, Math.min(centerIndex, carouselData.length - 1)));
    }
  };

  const handleMomentumScrollEnd = (event) => {
    const scrollX = event.nativeEvent.contentOffset.x;
    const viewWidth = event.nativeEvent.layoutMeasurement.width;
    const itemWidth = ITEM_WIDTH + SPACING;
    const contentWidth = itemWidth * carouselData.length;
    const maxOffset = contentWidth - viewWidth;

    // Leftmost boundary: Snap to first item
    if (scrollX <= 0) {
      setCurrentIndex(0);
      carouselRef.current.scrollTo({ x: 0, animated: true });
      return;
    }

    // Rightmost boundary: Snap to last item
    if (scrollX >= maxOffset - 1) {
      setCurrentIndex(carouselData.length - 1);
      carouselRef.current.scrollTo({ x: maxOffset, animated: true });
      return;
    }

    // Normal snapping behavior
    const centerPosition = scrollX + viewWidth / 2;
    const targetIndex = Math.round(centerPosition / itemWidth);
    const targetOffset = Math.max(0, targetIndex * itemWidth - (viewWidth - ITEM_WIDTH) / 2);
    const boundedOffset = Math.min(targetOffset, maxOffset);

    setCurrentIndex(Math.max(0, Math.min(targetIndex, carouselData.length - 1)));
    carouselRef.current.scrollTo({
      x: boundedOffset,
      animated: true,
    });
  };

  // Memoize itemStyle for the first item (uploadNewSelfie)
  const firstItemStyle = useMemo(() => {
    const distanceFromCenter = Math.abs(0 - currentIndex);
    const isCentered = currentIndex === 0;
    const scale = isCentered
      ? 1.2
      : Math.max(1.3 - distanceFromCenter * 0.15, 0.8);
    const zIndex = isCentered ? 1000 : Math.max(1000 - distanceFromCenter * 100, 50);

    return {
      transform: [{ scale }],
      zIndex,
      marginLeft: isDesktop() ? 0 : 20,
      marginRight: isDesktop() ? 0 : SPACING,
      transition: 'transform 0.3s ease-in-out',
      alignItems: 'center',
    };
  }, [currentIndex]);

  const contentContainerStyle = memoize(
    (itemCount) => {
      const isDesktopValue = isDesktop();
      return [
        styles.daysList,
        {
          paddingLeft: isDesktopValue ? (SCREEN_WIDTH - ITEM_WIDTH) / 2 : SPACING / 2,
          paddingRight: isDesktopValue
            ? (SCREEN_WIDTH - ITEM_WIDTH) / 2 + (ITEM_WIDTH + SPACING) * 2
            : SPACING / 2,
          width: (ITEM_WIDTH + SPACING) * itemCount + (isDesktopValue ? 0 : SPACING),
        },
      ];
    },
    (itemCount) => [itemCount]
  );

  const renderCarouselItem = memoize(
    ({ item, index }) => {
      if (item?.title === 'uploadNewSelfie') {
        return <UploadNewSelfie styling={firstItemStyle} onPress={navigateToSelfie} />;
      }

      const distanceFromCenter = Math.abs(index - currentIndex);
      const isCentered = index === currentIndex;
      const scale = isCentered
        ? 1.2
        : Math.max(1.3 - distanceFromCenter * 0.15, 0.8);
      const translateX = isCentered ? 0 : distanceFromCenter * -10 * (1 - scale);
      const zIndex = isCentered ? 1000 : Math.max(1000 - distanceFromCenter * 100, 50);
      const progressCircleRadius = isDesktop() ? 20 : 18;
      const progressCirlceWidth = isDesktop() ? 3 : 2;

      const itemStyle = {
        transform: [{ scale }],
        zIndex,
        marginLeft: isDesktop() ? (index !== 0 ? Math.min(-50 / 2, -35) : 0) : (index === 0) ? 20 : 0,
        marginRight: isDesktop() ? 0 : SPACING,
        transition: 'transform 0.3s ease-in-out',
        alignItems: 'center',
      };

      const handleClick = () => {
        handleClickOnSelfie(index - 1);
      };

      return (
        <TouchableOpacity style={itemStyle} onPress={handleClick}>
          <View key={index}>
            <FastImageView
              source={{ uri: item.image }}
              style={styles.image}
              resizeMode="cover"
            />
          </View>
          <View style={styles.scoreContainer}>
            <Text style={styles.carouselDateText}>
              {extractDate(item.created_at)}
            </Text>
            <View style={styles.overAllSkinScore}>
              <ProgressCircle
                percent={item?.skin_score}
                radius={progressCircleRadius}
                borderWidth={progressCirlceWidth}
                color={getColorForOverAllScore(item?.skin_score)}
                shadowColor={colors.white}
                bgColor={colors.white}
                outerCircleStyle={styles.outerCircleStyle}
              >
                <Text style={styles.headingText}>{item?.skin_score}%</Text>
              </ProgressCircle>
            </View>
          </View>
        </TouchableOpacity>
      );
    },
    ({ item, index }) => {
      // For uploadNewSelfie, exclude currentIndex from cache key
      if (item?.title === 'uploadNewSelfie') {
        return `${item.title}-${index}`;
      }
      return `${item?.title || item?.created_at}-${index}-${currentIndex}`;
    },
  );

  if (isBlank(carouselData)) {
    if (!showShimmer) return null;
    const shimmerStyles = [
      { style: styles.shimmerLeftOuter },
      { style: styles.shimmerLeftSmall },
      { style: styles.shimmerCenterLarge },
      { style: styles.shimmerRightSmall },
      { style: styles.shimmerRightOuter },
    ];
    const shimmerColors = [colors.shimmer_grey, colors.shimmer_white, colors.shimmer_grey];
    return (
      <View style={styles.shimmerContainer}>
        {shimmerStyles.map((item, index) => (
          <ShimmerPlaceHolder
            key={index}
            autoRun
            style={item.style}
            colorShimmer={shimmerColors}
          />
        ))}
      </View>
    );
  }

  return (
    <View style={styles.home}>
      <View style={styles.container}>
        <ScrollView
          ref={carouselRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          snapToAlignment="center"
          snapToInterval={ITEM_WIDTH + SPACING}
          decelerationRate="fast"
          scrollEventThrottle={16}
          onScroll={handleScroll}
          onMomentumScrollEnd={handleMomentumScrollEnd}
          contentContainerStyle={contentContainerStyle(carouselData.length)}
        >
          {carouselData.map((item, index) =>
            renderCarouselItem({ item, index })
          )}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  home: {
    width: '100%',
    alignItems: 'center',
    alignSelf: 'center',
    marginTop: 22,
    height: isDesktop() ? SCREEN_WIDTH * 0.3 : SCREEN_WIDTH * 0.7,
    paddingTop: 20,
  },
  container: {
    height: 300,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: -24,
    overflow: 'hidden',
  },
  image: {
    width: ITEM_WIDTH,
    height: ITEM_HEIGHT,
    borderRadius: ITEM_WIDTH / 2,
    borderWidth: 5,
    borderColor: colors.white,
  },
  daysList: {
    alignItems: 'center',
    height: 300,
    paddingLeft: (SCREEN_WIDTH - ITEM_WIDTH) / 2,
    paddingRight: (SCREEN_WIDTH - ITEM_WIDTH) / 2,
  },
  paddingView: {
    width: (SCREEN_WIDTH - ITEM_WIDTH) / 2,
  },
  carouselDateText: {
    fontFamily: 'Roboto-Regular',
    color: colors.black,
    fontWeight: '400',
  },
  carouselScoreText: {
    fontFamily: 'Roboto-Regular',
    color: colors.black,
    fontWeight: '700',
  },
  overAllSkinScore: {
    width: Math.min(36, SCREEN_WIDTH * 0.1),
    height: Math.min(36, SCREEN_WIDTH * 0.1),
    borderRadius: Math.min(20, SCREEN_WIDTH * 0.05),
    backgroundColor: 'white',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 6,
    marginTop: 6,
  },
  headingText: {
    fontFamily: 'Roboto',
    fontSize: isDesktop() ? 15 : 13,
    fontWeight: '700',
    color: colors.black,
  },
  shimmerContainer: {
    flexDirection: 'row',
    alignSelf: 'center',
    marginTop: 8,
  },
  shimmerLeftSmall: {
    width: Math.min(130, SCREEN_WIDTH * 0.35),
    height: Math.min(130, SCREEN_WIDTH * 0.35),
    borderRadius: Math.min(100, SCREEN_WIDTH * 0.35),
    alignSelf: 'center',
    marginRight: -60,
  },
  shimmerCenterLarge: {
    width: Math.min(180, SCREEN_WIDTH * 0.45),
    height: Math.min(180, SCREEN_WIDTH * 0.45),
    borderRadius: Math.min(100, SCREEN_WIDTH * 0.45),
    alignSelf: 'center',
    zIndex: 1,
  },
  shimmerRightSmall: {
    width: Math.min(130, SCREEN_WIDTH * 0.35),
    height: Math.min(130, SCREEN_WIDTH * 0.35),
    borderRadius: Math.min(100, SCREEN_WIDTH * 0.35),
    alignSelf: 'center',
    marginLeft: -60,
  },
  shimmerLeftOuter: {
    width: 110,
    height: 110,
    borderRadius: 100,
    alignSelf: 'center',
    marginRight: -60,
  },
  shimmerRightOuter: {
    width: 110,
    height: 110,
    borderRadius: 100,
    alignSelf: 'center',
    marginLeft: -60,
    zIndex: -1,
  },
  imageStyle: {
    width: 20,
    height: 20,
  },
  scoreContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginTop: 8,
  },
  uploadNewSelfieButton: {
    backgroundColor: 'lightgrey',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 30,
  },
  outerCircleStyle: {
    overflow: 'hidden',
  },
});

export default HistoryComponent;
